import { useState } from 'react'
import { AlertTriangle, MousePointerClick, Sparkles, Download, Check, X, Save } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import EmptyState from '@/components/common/EmptyState'
import { ESTADO_FILA } from './estados'
import { descargarRechazos, filasConProblema } from './rechazos'

const SIN_MAPEAR = '__sin__'

/** Cuántas filas con problema se listan en pantalla antes de mandar al CSV. */
const MAX_EN_PANTALLA = 25

/**
 * EL MAPEO, SOBRE LOS DATOS — no sobre una lista de nombres.
 *
 * El asistente de antes pedía asignar campos mirando una lista de encabezados y una fila de
 * muestra. Con eso, una columna `TIPO` cuyo ejemplo es `1` no dice absolutamente nada; con
 * `1, 1, 2, 1, 3` delante ya se ve que es un enum de tres valores. Por eso el `<Select>` va
 * EN LA CABECERA de una rejilla de previsualización con varias filas, y no en un formulario
 * aparte.
 *
 * Y las columnas sin asignar se ven grises EN TODA SU ALTURA. El aviso de "columnas que no
 * se usan" no es un texto al pie que nadie lee: es la rejilla.
 */
export default function SeccionDetalle({
  hoja,
  muestra = [],
  especificaciones,
  entidades = [],
  onCambiarEntidad,
  onCambiarMapeo,
  onEnsayar,
  ensayando,
  onSugerirIA,
  onDecidirSugerencia,
  sugiriendo,
  perfiles = [],
  onAplicarPerfil,
  onGuardarPerfil,
}) {
  const [nombrePerfil, setNombrePerfil] = useState('')
  const [guardandoPerfil, setGuardandoPerfil] = useState(false)
  if (!hoja) {
    // Solo se ve cuando el proyecto SÍ tiene hojas pero el asesor aún no eligió una en el
    // plan de la izquierda — si el proyecto no tiene ninguna hoja, PlanDeMigracion ya explica
    // eso por su lado, y no hace falta repetirlo aquí.
    return (
      <EmptyState
        icono={<MousePointerClick size={28} />}
        titulo="Elige una hoja"
        descripcion="Selecciona una sección del plan para ver y ajustar su mapeo."
      />
    )
  }
  const spec = especificaciones?.[hoja.entidad] || null
  const campos = (spec?.campos || []).filter((c) => !c.derivado && !c.sensible)
  const usados = new Set(Object.values(hoja.mapeo || {}))

  const asignar = (columna, campo) => {
    const siguiente = { ...(hoja.mapeo || {}) }
    if (campo === SIN_MAPEAR) delete siguiente[columna]
    else {
      // Un campo solo puede venir de una columna: dejar dos hace que gane la última en
      // silencio, y el cliente descubre el precio equivocado facturando.
      for (const [col, c] of Object.entries(siguiente)) if (c === campo) delete siguiente[col]
      siguiente[columna] = campo
    }
    onCambiarMapeo(siguiente)
  }

  const faltanRequeridos = (spec?.campos || [])
    .filter((c) => c.requerido && !c.derivado && !usados.has(c.clave))
    .map((c) => c.etiqueta)

  const problemas = filasConProblema(hoja.ensayo?.filas || [])

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-body-sm text-neutral-600 dark:text-neutral-400">Esta hoja es</span>
        <Select value={hoja.entidad || SIN_MAPEAR} onValueChange={(v) => onCambiarEntidad(v === SIN_MAPEAR ? null : v)}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Elige la sección" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={SIN_MAPEAR}>No la cargues</SelectItem>
            {entidades.map((e) => (
              <SelectItem key={e.clave} value={e.clave}>
                {e.etiqueta}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hoja.entidadOrigen === 'alias' && hoja.confianza > 0 && (
          <Badge variant="secondary">reconocida automáticamente</Badge>
        )}
        {hoja.entidadOrigen === 'ia' && <Badge variant="secondary">propuesta por la IA</Badge>}

        {/*
          Esto existía en el backend con su endpoint y su flujo de aceptar/descartar, y NO
          tenía ni un botón: la única forma de llegar era escribirle al copiloto «sugiéreme el
          mapeo de esta hoja». Un asesor no adivina eso.
        */}
        <Button variant="secondary" size="sm" className="ml-auto" onClick={onSugerirIA} disabled={sugiriendo}>
          <Sparkles size={15} className="mr-1.5" />
          {sugiriendo ? 'Pensando…' : 'Que la IA proponga el mapeo'}
        </Button>
      </div>

      {/*
        Lo que el lector descartó al abrir el archivo. El backend ya lo contaba y lo guardaba;
        no lo pintaba nadie, así que las filas desaparecían en silencio.
      */}
      {hoja.encabezadoDudoso && (
        <Alert variant="warning">
          <AlertDescription>
            <strong>La primera fila puede ser un dato, no un encabezado.</strong> Se tomó
            «{(hoja.encabezados || []).slice(0, 3).join(' · ')}» como nombres de columna, pero tiene la misma
            forma que las filas de abajo. Si es un dato, <strong>esa fila no se va a migrar</strong>: agrégale al
            archivo una fila de encabezados (por ejemplo «codigo, nombre, tipo») y vuelve a subirlo.
          </AlertDescription>
        </Alert>
      )}

      {(hoja.descartadasArriba > 0 || hoja.totalesAbajo > 0) && (
        <p className="text-caption text-neutral-500 dark:text-neutral-400">
          Al leer el archivo se saltaron{' '}
          {hoja.descartadasArriba > 0 && `${hoja.descartadasArriba} fila(s) de encabezado o título arriba`}
          {hoja.descartadasArriba > 0 && hoja.totalesAbajo > 0 && ' y '}
          {hoja.totalesAbajo > 0 && `${hoja.totalesAbajo} fila(s) de totales abajo`}.
        </p>
      )}

      {/* La IA propone, el asesor dispone: nada se aplica sin este clic. */}
      {hoja.sugerencia?.pendiente && (
        <Alert variant="info">
          <AlertDescription className="space-y-2">
            <p>
              La IA propone que esta hoja sea <strong>{entidades.find((e) => e.clave === hoja.sugerencia.entidad)?.etiqueta || hoja.sugerencia.entidad}</strong>
              {hoja.sugerencia.motivo ? ` — ${hoja.sugerencia.motivo}` : ''}.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => onDecidirSugerencia?.(true)}>
                <Check size={15} className="mr-1.5" /> Aplicar la propuesta
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onDecidirSugerencia?.(false)}>
                <X size={15} className="mr-1.5" /> Descartarla
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {faltanRequeridos.length > 0 && (
        <p className="flex items-start gap-1.5 text-body-sm text-warning-700 dark:text-warning-300">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          Falta asignar: {faltanRequeridos.join(', ')}. Sin eso, ninguna fila entra.
        </p>
      )}

      {/*
        Perfiles de mapeo: «el POS Vendex siempre exporta las mismas columnas». Guardarlo una
        vez ahorra repetir el mapeo entero en cada cliente que use ese mismo sistema. Estaba
        solo en el asistente plegado, así que en la pantalla donde se mapea de verdad no
        existía.
      */}
      {hoja.entidad && (
        <details className="rounded-lg border border-neutral-200 dark:border-neutral-800 px-3 py-2">
          <summary className="cursor-pointer select-none text-body-sm font-medium text-neutral-700 dark:text-neutral-300">
            Mapeos guardados {perfiles.length > 0 && `(${perfiles.length})`}
          </summary>
          <div className="mt-3 space-y-3">
            {perfiles.length > 0 ? (
              <ul className="space-y-1">
                {perfiles.map((p) => (
                  <li key={p._id} className="flex flex-wrap items-center gap-2 text-body-sm">
                    <span className="min-w-0 flex-1 truncate text-neutral-700 dark:text-neutral-300">{p.nombre}</span>
                    <span className="text-caption text-neutral-400 dark:text-neutral-500">
                      {Object.keys(p.mapeo || {}).length} columnas
                    </span>
                    <Button variant="secondary" size="xs" onClick={() => onAplicarPerfil?.(p)}>
                      Aplicar
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-body-sm text-neutral-500 dark:text-neutral-400">
                Todavía no has guardado ningún mapeo para esta sección.
              </p>
            )}

            <div className="flex flex-wrap items-center gap-2">
              {/* Antes esto era un `window.prompt()`, el único de toda la pantalla. */}
              <Input
                className="max-w-xs"
                placeholder="Nombre del mapeo (ej. «POS Vendex — productos»)"
                value={nombrePerfil}
                onChange={(e) => setNombrePerfil(e.target.value)}
              />
              <Button
                variant="secondary"
                size="sm"
                disabled={!nombrePerfil.trim() || guardandoPerfil || usados.size === 0}
                onClick={async () => {
                  setGuardandoPerfil(true)
                  try {
                    await onGuardarPerfil?.(nombrePerfil.trim())
                    setNombrePerfil('')
                  } finally {
                    setGuardandoPerfil(false)
                  }
                }}
              >
                <Save size={15} className="mr-1.5" /> Guardar este mapeo
              </Button>
            </div>
          </div>
        </details>
      )}

      {/* La rejilla scrollea sola; la página nunca se mueve de lado. */}
      <div className="overflow-x-auto rounded-lg border border-neutral-200 dark:border-neutral-800">
        <table className="w-full border-collapse text-body-sm">
          <thead>
            <tr className="bg-neutral-50 dark:bg-neutral-800/60">
              {(hoja.encabezados || []).map((col) => {
                const campo = (hoja.mapeo || {})[col]
                return (
                  <th key={col} className="min-w-[11rem] border-b border-neutral-200 dark:border-neutral-800 p-2 text-left align-top">
                    <p className="truncate text-caption font-semibold text-neutral-700 dark:text-neutral-300" title={col}>
                      {col}
                    </p>
                    <Select value={campo || SIN_MAPEAR} onValueChange={(v) => asignar(col, v)}>
                      <SelectTrigger className="mt-1 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SIN_MAPEAR}>— no se usa —</SelectItem>
                        {campos.map((c) => (
                          <SelectItem key={c.clave} value={c.clave}>
                            {c.etiqueta}
                            {c.requerido ? ' *' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {muestra.map((fila, i) => (
              <tr key={i} className="border-b border-neutral-100 dark:border-neutral-800 last:border-0">
                {(hoja.encabezados || []).map((col, j) => {
                  const sinUsar = !(hoja.mapeo || {})[col]
                  return (
                    <td
                      key={col}
                      // Gris en toda la altura: el aviso de columnas sin usar ES la rejilla.
                      className={cn('max-w-[16rem] truncate p-2', sinUsar && 'bg-neutral-50 dark:bg-neutral-800/60 text-neutral-400 dark:text-neutral-500')}
                      title={String(fila?.[j] ?? '')}
                    >
                      {String(fila?.[j] ?? '')}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/*
          Se deshabilita también por campos requeridos sin mapear: antes se podía lanzar el
          ensayo sabiendo de antemano que las filas iban a fallar todas, y el asesor leía una
          pantalla de errores que solo decían lo que el aviso amarillo ya decía.
        */}
        <Button onClick={onEnsayar} disabled={!hoja.entidad || faltanRequeridos.length > 0 || ensayando}>
          {ensayando ? 'Probando…' : 'Probar sin escribir'}
        </Button>
        {hoja.ensayo && (
          <span className="text-caption text-neutral-500 dark:text-neutral-400">
            {hoja.ensayo.crear} nuevas · {hoja.ensayo.actualizar} se actualizan · {hoja.ensayo.errores} no entran
          </span>
        )}
      </div>

      {problemas.length > 0 && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-body-sm font-medium text-neutral-900 dark:text-neutral-50">
              {problemas.length > MAX_EN_PANTALLA
                ? `Mostrando ${MAX_EN_PANTALLA} de ${problemas.length} filas con problema`
                : `${problemas.length} fila(s) con problema`}
            </span>
            {/* Corregir el archivo original mirando una lista recortada en pantalla no es viable. */}
            <Button variant="ghost" size="xs" onClick={() => descargarRechazos(problemas, hoja.nombreHoja)}>
              <Download size={14} className="mr-1" /> Descargar todas en CSV
            </Button>
          </div>
          <ul className="space-y-1">
            {problemas.slice(0, MAX_EN_PANTALLA).map((f) => (
              <li key={f.fila} className="flex flex-wrap items-baseline gap-2 text-body-sm">
                <span className="tabular-nums text-neutral-400 dark:text-neutral-500">fila {f.fila}</span>
                <Badge variant={ESTADO_FILA[f.estado]?.variante || 'secondary'}>
                  {ESTADO_FILA[f.estado]?.etiqueta || f.estado}
                </Badge>
                <span className="text-neutral-600 dark:text-neutral-400">{f.motivos?.join(' · ')}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
